import { useState } from "react";
import type { Entity, LoadResponse } from "@lagerlog/core";
import { api, store } from "../store.ts";
import { STARTER, fillWithDemo } from "../account.ts";
import { APP } from "../brand.ts";

/** Access of the spike: a fixed token per workspace instead of an account, and the storages this device holds. */
export function Setup() {
  const [token, setToken] = useState("");
  const [device, setDevice] = useState(() => `Gerät ${Math.floor(Math.random() * 900 + 100)}`);
  const [workspace, setWorkspace] = useState<string | null>(null);
  const [lagers, setLagers] = useState<{ id: string; name: string }[]>([]);
  const [chosen, setChosen] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function check() {
    setError(null);
    setBusy(true);
    try {
      const me = await api<{ workspace: { name: string } }>({ token }, "/api/me");
      const load = await api<LoadResponse>({ token }, "/api/load?lager=");
      const found = load.entities
        .filter((e: Entity) => e.type === "lager")
        .map((e) => ({ id: e.id, name: typeof e.fields.name === "string" ? e.fields.name : e.id }));
      setWorkspace(me.workspace.name);
      setLagers(found);
      setChosen(new Set(found.map((l) => l.id)));
    } catch (err) {
      setError(err instanceof Error && "status" in err && err.status === 401 ? "Dieser Zugang ist nicht gültig." : "Keine Verbindung zum Server.");
    } finally {
      setBusy(false);
    }
  }

  async function connect() {
    await store.connect({ token, workspace: workspace!, lagers: [...chosen], device });
    location.hash = "#/buchen";
  }

  /** An empty workspace gets the invented Weinbar Rebstock (fillWithDemo). */
  async function demo() {
    await store.connect({ token, workspace: workspace!, lagers: STARTER.map((l) => l.id), device });
    await fillWithDemo();
    location.hash = "#/buchen";
  }

  return (
    <div className="setup">
      <h1>{APP.short}</h1>
      <p className="muted">Mit einem Zugang aus der Testphase (ohne Konto). Zugang und Lager wählen, dann geht alles auch ohne Netz.</p>
      <p>
        <a href="#/anmelden">Zurück zur Anmeldung</a>
      </p>
      {!workspace ? (
        <>
          <label>
            Zugang
            <input value={token} onChange={(e) => setToken(e.target.value.trim())} autoComplete="off" data-testid="token" />
          </label>
          <label>
            Name dieses Geräts
            <input value={device} onChange={(e) => setDevice(e.target.value)} />
          </label>
          <button className="primary" disabled={!token || busy} onClick={check}>
            Weiter
          </button>
        </>
      ) : (
        <>
          <p>
            Arbeitsbereich <strong>{workspace}</strong>
          </p>
          {lagers.length === 0 ? (
            <>
              <p className="muted">Hier gibt es noch keine Lager.</p>
              <button className="primary" onClick={demo} data-testid="demo">
                Demo-Weinbar anlegen
              </button>
            </>
          ) : (
            <>
              <p>Welche Lager soll dieses Gerät führen?</p>
              {lagers.map((l) => (
                <label key={l.id} className="check">
                  <input
                    type="checkbox"
                    checked={chosen.has(l.id)}
                    onChange={(e) => {
                      const next = new Set(chosen);
                      if (e.target.checked) next.add(l.id);
                      else next.delete(l.id);
                      setChosen(next);
                    }}
                  />
                  {l.name}
                </label>
              ))}
              <button className="primary" disabled={chosen.size === 0} onClick={connect} data-testid="connect">
                Los geht's
              </button>
            </>
          )}
        </>
      )}
      {error && <p className="error">{error}</p>}
    </div>
  );
}
